import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import MarkdownIt from 'markdown-it';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';

const mdParser = new MarkdownIt();

const TaskForm = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        difficulty_level: 'beginner',
        categories: [],
        image: null,
        answer_key: '',
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const baseUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (slug) {
            setIsEditMode(true);
            fetchTask();
        }
    }, [slug]);

    const fetchTask = async () => {
        try {
            const response = await api.get(`/api/tasks/by-slug/${slug}`);
            const task = response.data;
            
            setFormData({
                title: task.title,
                description: task.description,
                difficulty_level: task.difficulty_level,
                categories: task.categories || [],
                answer_key: task.answer_key,
                image: task.image_url || null
            });

            if (task.image_url) {
                if (task.image_url.startsWith('https://res.cloudinary.com')) {
                    setImagePreview(task.image_url);
                } else {
                    const imageUrl = task.image_url.startsWith('/') ? task.image_url : `/${task.image_url}`;
                    setImagePreview(`${baseUrl}${imageUrl}`);
                }
            }
        } catch (error) {
            console.error('Error fetching task:', error);
            toast.error('Failed to fetch task');
            navigate('/tasks');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const form = new FormData();
            form.append('title', formData.title);
            form.append('description', formData.description);
            form.append('difficulty_level', formData.difficulty_level);
            form.append('categories', JSON.stringify(formData.categories));
            form.append('answer_key', formData.answer_key);
            if (formData.image instanceof File) {
                form.append('image', formData.image);
            }

            let response;
            if (isEditMode) {
                response = await api.put(`/api/tasks/by-slug/${slug}`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Task updated successfully');
            } else {
                response = await api.post('/api/tasks', form, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Task created successfully');
            }

            navigate('/tasks');
        } catch (error) {
            console.error('Error saving task:', error);
            toast.error(error.response?.data?.error || 'Failed to save task');
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, image: file }));
            
            // Show preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleEditorChange = ({ text }) => {
        setFormData(prev => ({ ...prev, description: text }));
    };

    return (
        <div className="bg-[#151515] border border-[#FF3366]/10 rounded-xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-white mb-8">
                {isEditMode ? 'Edit Task' : 'Create New Task'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title Input */}
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Title</label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-[#FF3366]/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#FF3366] focus:ring-1 focus:ring-[#FF3366] transition-all duration-200"
                        placeholder="Enter task title"
                        required
                    />
                </div>

                {/* Description Input - replace with Markdown Editor */}
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Description (Markdown)</label>
                    <div className="markdown-editor-container rounded-xl overflow-hidden border-2 border-gray-700">
                        <MdEditor
                            value={formData.description}
                            renderHTML={text => mdParser.render(text)}
                            onChange={handleEditorChange}
                            style={{ height: '400px' }}
                            config={{
                                view: {
                                    menu: true,
                                    md: true,
                                    html: true
                                },
                                canView: {
                                    menu: true,
                                    md: true,
                                    html: true,
                                    fullScreen: true,
                                    hideMenu: true
                                }
                            }}
                            placeholder="Write your task description in Markdown format..."
                        />
                    </div>
                </div>

                {/* Answer Key */}
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Answer Key</label>
                    <textarea
                        value={formData.answer_key}
                        onChange={(e) => setFormData({ ...formData, answer_key: e.target.value })}
                        className="w-full bg-[#111827] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all duration-200 min-h-[120px]"
                        placeholder="Enter the expected answer or solution"
                        required
                    />
                </div>

                {/* Difficulty Level */}
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Difficulty Level</label>
                    <div className="bg-[#111827] border border-gray-700 p-2 rounded-xl">
                        {['beginner', 'intermediate', 'advanced'].map((level) => (
                            <button
                                key={level}
                                type="button"
                                onClick={() => setFormData({ ...formData, difficulty_level: level })}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    formData.difficulty_level === level
                                        ? 'bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white'
                                        : 'text-gray-500 hover:text-[#FF3366] hover:bg-gray-700'
                                }`}
                            >
                                {level.charAt(0).toUpperCase() + level.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Categories */}
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Categories</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                        {['HTML', 'CSS', 'JavaScript', 'DOM', 'Responsive Design', 'Web APIs'].map((category) => (
                            <button
                                key={category}
                                type="button"
                                onClick={() => {
                                    const newCategories = formData.categories.includes(category)
                                        ? formData.categories.filter(c => c !== category)
                                        : [...formData.categories, category];
                                    setFormData({ ...formData, categories: newCategories });
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    formData.categories.includes(category)
                                        ? 'bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white border border-[#FF3366]/70'
                                        : 'bg-[#111827] text-gray-500 border border-gray-700 hover:text-[#FF3366] hover:bg-gray-700'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Image Upload */}
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Image</label>
                    <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-700 border-dashed rounded-xl cursor-pointer bg-[#111827] hover:bg-gray-700 transition-all duration-200">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <svg className="w-8 h-8 mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500">
                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                </p>
                            </div>
                            <input
                                type="file"
                                onChange={handleImageChange}
                                accept="image/*"
                                className="hidden"
                            />
                        </label>
                    </div>
                    {imagePreview && (
                        <div className="mt-4">
                            <img 
                                src={imagePreview} 
                                alt="Preview" 
                                className="max-h-40 rounded-xl border border-gray-800/50"
                            />
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 pt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/tasks')}
                        className="px-6 py-3 bg-gray-800 text-gray-300 rounded-xl border border-gray-700 hover:text-white hover:border-gray-600 transition-all duration-200"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-xl hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 shadow-lg shadow-[#FF3366]/20 font-medium disabled:opacity-50"
                    >
                        {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Task' : 'Create Task')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TaskForm; 